import {
  CheckCircleIcon,
  ChevronLeftIcon,
  XIcon,
} from '@heroicons/react/outline';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import TickOnboarding from 'assets/Icons/TickOnboarding';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { twMerge } from 'tailwind-merge';

import { EventDetails } from '@/hooks/useClickEventTracking';
import { getScreenName } from '@/hooks/usePageTracking';

import BestValueBadge from '@/components/PlatformSubscription/BestValueBadge';
import LimitedTimeBadge from '@/components/PlatformSubscription/LimitedTimeButton';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import {
  platformRazorpayCloseModalEvent,
  platformSubscriptionCheckoutEvent,
  platformSubscriptionSuccessfulEvent,
} from '@/components/shared/Gtags';
import { LoadingV2 } from '@/components/shared/LoadingV2';
import { notify } from '@/components/shared/notify';

import { user } from '@/atoms/user';
import { getSubPlanNameLog } from '@/pages/gems-transactions';
import { startSubscription, verifySubscription } from '@/services/Subscribe';
import { getUserProfile } from '@/services/user';
import { isPlatformSubscriptionActive } from '@/utils/index';
import { IPlan, IStartSubscriptionBody } from '@/utils/interfaces/Subscription';
import { calculateDaysFromInterval, globalSubscriptionEvent } from '@/utils/Subscription';

interface Props {
  subscriptionSuccess: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setSubscriptionSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  plan: IPlan | undefined;
  className?: string;
  successClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
  cancelClassName?: string;
  phoneHeaderClassName?: string;
  paymentButtonClassName?: string;
  closeSuccessIconClassName?: string;
  setBeforePurchasse?: React.Dispatch<
    React.SetStateAction<{
      trigger: boolean;
      authSuccess: boolean;
    }>
  >;
  beforePurchase?: {
    trigger: boolean;
    authSuccess: boolean;
  };
  token?: boolean;
  onSubscriptionSuccess?: () => void;
  authLoading?: boolean;
}
const PlanInfo: FC<Props> = ({
  subscriptionSuccess,
  setOpen,
  setSubscriptionSuccess,
  plan,
  className,
  successClassName,
  headerClassName,
  contentClassName,
  cancelClassName,
  setBeforePurchasse,
  beforePurchase,
  token,
  phoneHeaderClassName,
  onSubscriptionSuccess,
  paymentButtonClassName,
  closeSuccessIconClassName,
  authLoading,
}) => {
  const screens = useBreakpoint();
  const [loading, setLoading] = useState<boolean>(false);
  const physical = plan?.features[0]?.periodicity && plan.rules[0]?.physical;
  const pathname = usePathname();
  const router = useRouter();
  const isAffiliate =
    router?.pathname?.includes('affiliate') ||
    pathname?.includes('affiliate');
  const [userData, setUserData] = useRecoilState(user);
  const [polling, setPolling] = useState(false);


  const platformSubscriptionCheckoutNitroEvent = (data: {
    subscription_id: string;
    plan_id: number;
    plan_price: number;
    plan_cycle: number
  }) => {
    try {
      if (
        typeof (window as any).nitro !== 'undefined' &&
        typeof (window as any).nitro.track === 'function'
      ) {
        const currentUrl = window.location.href;

        const zebra_selected_country = JSON?.parse(
          localStorage?.getItem?.('zebra_selected_country') as string,
        )?.value?.country;
        const utmData: Record<string, string> = {};
        const searchParams = new URLSearchParams(window?.location?.search);
        searchParams.forEach((value, key) => {
          if (key.startsWith('utm_')) {
            utmData[key] = value;
          }
        });
        const platformSubscriptionCheckoutNitroEventData = {
          page_url: currentUrl,
          plan_id: data.plan_id,
          plan_price: data.plan_price / 100,
          plan_cycle: data.plan_cycle,
          platform: 'website',
          country: zebra_selected_country?.name,
          currency: zebra_selected_country?.currency,
          utm_data: utmData,
        };

        nitro.track('platform_subscription_checkout', platformSubscriptionCheckoutNitroEventData);
      }
    } catch (error) {
      console.error(error);
    }
  };
  const platformSubscriptionFailedNitroEvent = (data: {
    subscription_id: string;
    plan_id: number;
    plan_price: string;
    plan_cycle: number
  }) => {
    try {
      if (
        typeof (window as any).nitro !== 'undefined' &&
        typeof (window as any).nitro.track === 'function'
      ) {
        const currentUrl = window.location.href;

        const zebra_selected_country = JSON?.parse(
          localStorage?.getItem?.('zebra_selected_country') as string,
        )?.value?.country;
        const utmData: Record<string, string> = {};
        const searchParams = new URLSearchParams(window?.location?.search);
        searchParams.forEach((value, key) => {
          if (key.startsWith('utm_')) {
            utmData[key] = value;
          }
        });
        const platformSubscriptionFailedNitroEventData = {
          page_url: currentUrl,
          plan_id: data.plan_id,
          plan_price: parseInt(data.plan_price) / 100,
          plan_cycle: data.plan_cycle,
          platform: 'website',
          country: zebra_selected_country?.name,
          currency: zebra_selected_country?.currency,
          utm_data: utmData,
          status: "failed",

        };

        nitro.track('platform_subscription_failed', platformSubscriptionFailedNitroEventData);
      }
    } catch (error) {
      console.error(error);
    }
  };
  const platformSubscriptionSuccessfulNitroEvent = (data: {
    subscription_id: string;
    plan_id: number;
    plan_price: string;
    plan_cycle: number
  }) => {
    try {
      if (
        typeof (window as any).nitro !== 'undefined' &&
        typeof (window as any).nitro.track === 'function'
      ) {
        const currentUrl = window.location.href;

        const zebra_selected_country = JSON?.parse(
          localStorage?.getItem?.('zebra_selected_country') as string,
        )?.value?.country;
        const utmData: Record<string, string> = {};
        const searchParams = new URLSearchParams(window?.location?.search);
        searchParams.forEach((value, key) => {
          if (key.startsWith('utm_')) {
            utmData[key] = value;
          }
        });
        const platformSubscriptionSuccessfulNitroEventData = {
          page_url: currentUrl,
          plan_id: data.plan_id,
          plan_price: parseInt(data.plan_price) / 100,
          plan_cycle: data.plan_cycle,
          platform: 'website',
          country: zebra_selected_country?.name,
          currency: zebra_selected_country?.currency,
          utm_data: utmData,
        };

        nitro.track('platform_subscription_successful', platformSubscriptionSuccessfulNitroEventData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const checkoutEvent = (data: {
    subscription_id: string;
    plan_id: number;
    plan_price: number;
    plan_cycle: number
  }) => {
    try {
      const eventParams: EventDetails = {
        event_name: 'platform_subscription_checkout',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digitalcopy',
        source_id: data?.subscription_id.toString(),
        sub_source: 'plan',
        sub_source_id: data.plan_id.toString(),
        sub_source_name: getSubPlanNameLog(plan?.name ?? ''),
        source_name: 'platform_subscription',
        ...globalSubscriptionEvent(plan)?.event,
        meta: {
          click_source: getScreenName(pathname),
          ...globalSubscriptionEvent(plan)?.meta,
        },
      };

      platformSubscriptionCheckoutNitroEvent({
        subscription_id: data.subscription_id,
        plan_id: data.plan_id,
        plan_price: data.plan_price,
        plan_cycle: data.plan_cycle
      })


      platformSubscriptionCheckoutEvent({
        payload: eventParams,
        userData,
        pathname,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const successEvent = (data: { subscription_id: string, plan_cycle: number, plan_id: number, plan_price: string }) => {
    try {




      const eventParams: EventDetails = {
        event_name: 'platform_subscription_successful',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digitalcopy',
        source_id: data.subscription_id,
        sub_source: 'plan',
        sub_source_id: plan?.id.toString(),
        sub_source_name: getSubPlanNameLog(plan?.name ?? ''),
        source_name: 'platform_subscription',
        ...globalSubscriptionEvent(plan)?.event,
        meta: {
          click_source: getScreenName(pathname),
          ...globalSubscriptionEvent(plan)?.meta,
        },
      };

      platformSubscriptionSuccessfulNitroEvent({ subscription_id: data.subscription_id, plan_cycle: data.plan_cycle, plan_id: data.plan_id, plan_price: data.plan_price })

      platformSubscriptionSuccessfulEvent({
        payload: eventParams,
        userData,
        pathname,
      });
    } catch (error) {
      console.error(error);
    }
  };
  const startSubscriptionHandle = async () => {
    try {
      setLoading(true);
    } catch (error) {
      console.error(error);
    }
  };
  const razorpayCloseModalEvent = (data: {
    subscription_id: string;
    plan_id: number;
    plan_cycle: number, plan_price: string
  }) => {
    try {
      const eventParams: EventDetails = {
        event_name: 'platform_subscription_failed',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digitalcopy',
        source_id: data?.subscription_id.toString(),
        sub_source: 'plan',
        sub_source_id: plan?.id.toString(),
        sub_source_name: getSubPlanNameLog(plan?.name ?? ''),
        source_name: 'platform_subscription',
        ...globalSubscriptionEvent(plan)?.event,
        meta: {
          click_source: getScreenName(pathname),
          ...globalSubscriptionEvent(plan)?.meta,
        },
      };

      platformSubscriptionFailedNitroEvent({
        plan_cycle: data.plan_cycle,
        plan_id: data.plan_id,
        plan_price: data.plan_price,
        subscription_id: data.subscription_id
      })

      platformRazorpayCloseModalEvent({
        payload: eventParams,
        userData,
        pathname,
      });
    } catch (error) {
      console.error(error);
    }
  };
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (typeof document === 'undefined') {
        resolve(false);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const successSubscription = async () => {
    setSubscriptionSuccess(true);
    setLoading(false);

    const data = await getUserProfile();
    setUserData(data?.data);
  };
  const handleSubscribe = async (data: IStartSubscriptionBody) => {
    try {
      setLoading(true);
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        notify('Razorpay SDK failed to load. Are you online?', 'error');
        return;
      }
      const result = await startSubscription(data);
      checkoutEvent({
        plan_id: plan?.id as number,
        subscription_id:
          result?.data?.subscription_id?.toString(),
        plan_price: ((plan?.razorpay_plan.amount) as number),
        plan_cycle: calculateDaysFromInterval((plan?.periodicity || 0), (plan?.periodicity_type || ''))
      });
      if (!process.env.NEXT_PUBLIC_RAZORPAY_KEY_2) {
        throw new Error('Missing RAZORPAY_KEY');
      }
      const options = {
        key: process.env.NEXT_PUBLIC_RAZORPAY_KEY_2 || '',
        ...(result.data.mode === 'auto'
          ? { subscription_id: result.data.razorpay.subscription_id }
          : {}),
        order_id: result.data.razorpay.order_id,
        name: 'Zebralearn',
        description: 'Subscription',
        prefill: {
          name: userData?.name,
          email: userData?.email,
        },
        handler: async function (response: {
          razorpay_payment_id: string;
          razorpay_subscription_id: string;
          razorpay_signature: string;
        }) {
          try {
            successEvent({
              subscription_id:
                result?.data?.subscription_id.toString(),
              plan_price: (plan?.razorpay_plan?.amount)?.toString() || '',
              plan_cycle: calculateDaysFromInterval((plan?.periodicity || 0), (plan?.periodicity_type || '')),
              plan_id: plan?.id as number,
            });
            setLoading(true);
            const verifyWithRetry = async () => {
              let shouldRetry = true;
              let verifyResult: any;

              while (shouldRetry) {
                verifyResult = await verifySubscription({
                  rp_payment_id: response.razorpay_payment_id,
                  ...(result.data.mode === 'auto'
                    ? {
                      razorpay_subscription_id:
                        response.razorpay_subscription_id,
                    }
                    : {}),
                });

                const retry = verifyResult?.data?.retry;
                const retryAfter = verifyResult?.data?.retry_after;

                if (retry && retryAfter) {
                  const waitMs = retryAfter * 1000;
                  await new Promise((res) => setTimeout(res, waitMs));
                  continue;
                }

                // Exit loop
                shouldRetry = false;
              }

              return verifyResult;
            };
            await verifyWithRetry();
            await startSubscriptionHandle();
            await successSubscription();
            onSubscriptionSuccess?.();
          } catch (error: any) {
            notify(formateErrorObject(error), 'error');
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            razorpayCloseModalEvent({
              plan_id: plan?.id as number,
              subscription_id:
                result.data.mode === 'auto'
                  ? result.data.razorpay.subscription_id
                  : result.data.razorpay.order_id,
              plan_price: (plan?.razorpay_plan?.amount)?.toString() || '',
              plan_cycle: calculateDaysFromInterval((plan?.periodicity || 0), (plan?.periodicity_type || '')),
            });
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const subscriptionHandle = async () => {
    if (token !== undefined) {
      if (token === false) {
        if (setBeforePurchasse && beforePurchase) {
          if (
            beforePurchase.authSuccess === false ||
            beforePurchase.trigger === false
          ) {
            return;
          }
        }
      }
    }
    if (!plan?.id) return;

    const data: IStartSubscriptionBody = {
      plan_id: plan?.id,
      change: false,
      immediate: false,
      mode: 'auto',
    };

    if (isPlatformSubscriptionActive(userData)) {
      data.change = true;
      data.immediate = true;
    }

    handleSubscribe(data);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!loading) return;

      e.preventDefault();

      // Setting an empty string is required for cross-browser support.
      // Browsers will show their own warning message.
      e.returnValue = '';

      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [loading]);

  useEffect(() => {
    if (
      beforePurchase?.authSuccess &&
      beforePurchase?.trigger &&
      setBeforePurchasse
    ) {
      subscriptionHandle();
      setBeforePurchasse((e) => ({ ...e, trigger: false }));
    }
  }, [beforePurchase, token]);

  return (
    <div>
      {polling ? (
        <div className='relative md:block flex flex-col items-center justify-center  border md:border-white/10 border-black md:rounded-2xl md:p-6 p-0 md:bg-grayZl-950 bg-black md:h-auto h-dvh'>
          <LoadingV2
            loaderColor='white'
            messages={[
              'Preparing your subscription benefits...',
              'Activating your premium access...',
              'Setting up your book deliveries...',
              'Finalizing your subscription...',
              'Unlocking exclusive content for you...',
              'Almost ready to dive into your books!',
              'You’re all set—just a moment more!',
            ]}
          />
        </div>
      ) : subscriptionSuccess ? (
        <div
          className={twMerge(
            'relative md:block flex flex-col items-center justify-center  border md:border-white/10 border-black md:rounded-2xl md:px-6 md:py-20 p-0 md:bg-grayZl-950 bg-black md:h-auto h-dvh',
            successClassName,
          )}
        >
          <div
            className={twMerge(
              'absolute top-0 right-0 m-5',
              closeSuccessIconClassName,
            )}
          >
            <ButtonDefault
              onClick={() => {
                setOpen?.(false);
                setPolling(false);
                setSubscriptionSuccess(false);
              }}
              variant={ButtonVariants.UNSTYLED}
              size={0}
              roundedUnstyled
            >
              <XIcon
                className={`
                
                w-6 h-6 text-white`}
              />
            </ButtonDefault>
          </div>
          <div className=' w-full flex-1 flex flex-col justify-center'>
            <div className='flex justify-center md:pb-8'>
              <TickOnboarding width={screens.md ? '100' : '64'} />
            </div>
            <span className='text-xl md:text-2xl xl:text-3xl  text-start flex justify-center md:text-center font-semibold text-white'>
              Congratulations{' '}
              <p className='text-xl md:text-3xl xl:text-4xl inline-flex pl-2 -translate-y-0.5 md:-translate-y-2'>
                🎉
              </p>
            </span>

            <div className='text-white/70 text-xs flex  justify-center md:text-lg  lg:text-center w-full '>
              Subscription completed successfully.
            </div>
          </div>
        </div>
      ) : (
        <div
          className={twMerge(
            'md:block flex flex-col  border md:border-white/10 border-black md:rounded-2xl md:p-6 p-0 md:bg-grayZl-950 bg-black md:h-auto h-dvh',
            className,
          )}
        >
          <div
            className={twMerge(
              'py-[11px] pl-2 pr-3 md:hidden flex items-center gap-3',
              phoneHeaderClassName,
            )}
          >
            <ButtonDefault
              onClick={() => setOpen?.(false)}
              variant={ButtonVariants.UNSTYLED}
              size={0}
              roundedUnstyled
            >
              <ChevronLeftIcon className='w-[22px] h-[22px] text-white' />
            </ButtonDefault>
            <p className='text-sx leading-[22px] font-medium text-white'>
              Subscription
            </p>
          </div>
          <div className={twMerge('flex-1 overflow-auto', contentClassName)}>
            <div
              className={twMerge(
                'md:flex hidden items-start justify-between mb-6 ',
                headerClassName,
              )}
            >
              {plan?.name === 'Standard' ? (
                <LimitedTimeBadge />
              ) : plan?.name === 'Premium' ? (
                <BestValueBadge />
              ) : (
                <LimitedTimeBadge classes={{ parantClass: 'invisible' }} />
              )}
              <ButtonDefault
                className={`${loading ? ' hidden ' : ''}`}
                onClick={() => {
                  setOpen?.(false);
                  setPolling(false);
                  setSubscriptionSuccess(false);
                }}
                variant={ButtonVariants.UNSTYLED}
                size={0}
                roundedUnstyled
              >
                <XIcon className='w-6 h-6 text-white' />
              </ButtonDefault>
            </div>
            <div className='flex flex-col md:gap-2.5 gap-2 md:items-center items-start md:mx-0 md:mt-0 mx-4 mt-6'>
              <p className='md:text-base/6 text-xs/5 font-normal text-primary '>
                Selected Plan
              </p>
              <div className='flex items-center gap-2'>
                <p className='md:text-[40px] text-2xl font-semibold md:leading-[-1px] leading-8 text-white '>
                  {plan?.name == 'Affiliate' ? "Prime" : plan?.name}
                </p>
                <div className='md:hidden block '>
                  {plan?.name === 'Standard' ? (
                    <LimitedTimeBadge
                      classes={{
                        textClass: ' text-xs/5 ',
                        parantClass: 'px-2.5 py-0.5',
                      }}
                    />
                  ) : plan?.name === 'Premium' ? (
                    <BestValueBadge
                      classes={{
                        textClass: ' text-xs/5 ',
                        textParant: 'px-2.5 py-0.5 m-px',
                      }}
                    />
                  ) : (
                    <LimitedTimeBadge classes={{ parantClass: 'invisible' }} />
                  )}
                </div>
              </div>
              {plan?.razorpay_plan.amount ? (
                <div className='flex gap-1.5 items-end'>
                  <div className='flex items-start gap-1'>
                    <p className='text-base text-white md:mt-1 mt-px'>₹</p>
                    <p className='md:text-[32px] text-xl font-semibold md:leading-10 leading-7 text-white'>
                      {plan?.razorpay_plan.amount / 100}
                    </p>
                  </div>
                  {isAffiliate && (
                    <p className='line-through text-white/50 md:text-2xl text-base font-medium self-end md:leading-[31px] leading-6'>
                      4999
                    </p>
                  )}
                  {
                    !isAffiliate ? <p className='font-normal md:text-base text-xs md:leading-[31px] leading-6 text-white/70'>
                      / {plan.periodicity}
                      {plan.periodicity_type === 'monthly' ? ' Months' : ''}
                    </p> : null
                  }

                </div>
              ) : null}
              {plan?.razorpay_plan.amount && plan.periodicity ? (
                <p className='text-base font-normal text-white'>
                  [ Unlimited Access /{' ' + plan.periodicity.toFixed(0) + ' '}
                  {isAffiliate ? (plan.periodicity_type === 'yearly' ? 'year' : plan.periodicity_type) : 'month'} ]
                </p>
              ) : (
                <></>
              )}
            </div>
            <div className='border-t border-dashed border-white/20 md:my-12 my-5 md:mx-14 mx-4 flex' />
            <div className='flex md:justify-center justify-start md:mb-12 mb-0 md:mx-0 mx-4'>
              <div className='flex flex-col gap-6'>
                <p className='uppercase text-white/40 md:text-sm text-xs leading-[22px] font-medium'>
                  What’s included
                </p>
                <ul className='flex flex-col gap-3'>
                  <li>
                    <div className='flex items-start gap-2.5'>
                      <CheckCircleIcon className='w-6 h-6 text-success-500' />
                      <div>
                        <p className='text-sm font-normal text-white leading-[22px] capitalize'>
                          Unlimited access to digital books
                        </p>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='flex items-start gap-2.5'>
                      <CheckCircleIcon className='w-6 h-6 text-success-500' />
                      <div>
                        <p className='text-sm font-normal text-white leading-[22px] capitalize'>
                          Access on any device
                        </p>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='flex items-start gap-2.5'>
                      <CheckCircleIcon className='w-6 h-6 text-success-500' />
                      <div>
                        <p className='text-sm font-normal text-white leading-[22px] capitalize'>
                          Easy learn with audiobook
                        </p>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className='flex items-start gap-2.5'>
                      <CheckCircleIcon className='w-6 h-6 text-success-500' />
                      <div>
                        <p className='text-sm font-normal text-white leading-[22px] capitalize'>
                          Video summary included
                        </p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className='flex items-center gap-3 md:pb-8 md:px-8 px-4 py-5 md:border-none border-t-[0.5px] border-grayZl-850'>
            <ButtonDefault
              onClick={() => setOpen?.(false)}
              className={twMerge(
                `
               
                border border-grayZl-850 md:flex hidden px-6 py-[9px] rounded-lg`,
                loading ? ' hidden md:hidden' : '',
                cancelClassName,
              )}
              variant={ButtonVariants.UNSTYLED}
              size={0}
              roundedUnstyled
            >
              Cancel
            </ButtonDefault>
            <ButtonDefault
              disabled={loading || authLoading}
              loading={loading || authLoading}
              variant={ButtonVariants.PRIMARY}
              size={0}
              className={twMerge(
                'flex flex-1 truncate px-6 py-[9px] rounded-lg',
                paymentButtonClassName,
              )}
              onClick={() => {
                setBeforePurchasse?.((e) => ({ ...e, trigger: true }));
                subscriptionHandle();
              }}
              roundedUnstyled
            >
              {/* {loading ? (
              <Loading3QuartersOutlined className='animate-spin w-5 h-5' />
            ) : null} */}
              Continue to Payment Gateway
            </ButtonDefault>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanInfo;
